import React, { useEffect, useState } from 'react';
import {
  getRideOperations, getRideOperation, createRideAdjustment, reverseRideAdjustment,
  getAdminPayouts, updateAdminPayoutStatus, getAdminClaims, updateAdminClaimStatus,
} from '../api/admin';

// Ride Operations (Kayd, Development Plan 04.11.2026 §5): ride overview
// (requested / assigned / ongoing / completed), driver status, pickup arrival
// and running waiting time, payment status, problem cases (no-show, missing
// cash confirmation, discrepant amounts) and the ride details with the
// history of status changes. Admin interventions are logged by the server.
const money = (c, currency) => (c == null ? '—' : `${currency || ''} ${(Number(c) / 100).toFixed(2)}`.trim());
const person = (u) => u?.full_name || u?.email || '—';
const when = (v) => (v ? new Date(v).toLocaleString() : '—');
const mmss = (sec) => (sec == null ? '—' : `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')} min`);
const cur = (r) => r?.fee_currency || r?.currency_code || '';

export default function RideOperations() {
  const [tab, setTab] = useState('rides');
  const [filters, setFilters] = useState({});
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [adjustment, setAdjustment] = useState({ target: 'customer', amount: '', reason: '' });

  const load = async (f = filters) => {
    setError('');
    setLoading(true);
    try {
      if (tab === 'rides') setRows((await getRideOperations(f)).data?.rides || []);
      else if (tab === 'payouts') setRows((await getAdminPayouts({})).data?.payouts || []);
      else setRows((await getAdminClaims({})).data?.claims || []);
    } catch (e) { setError(e.response?.data?.error || 'Could not load ride operations.'); setRows([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const setFilter = (k, v) => { const next = { ...filters, [k]: v }; setFilters(next); load(next); };
  const openRide = async (id) => {
    try { setSelected((await getRideOperation(id)).data); }
    catch (e) { setError(e.response?.data?.error || 'Could not load ride detail.'); }
  };
  const updateStatus = async (id, value) => {
    try {
      if (tab === 'payouts') await updateAdminPayoutStatus(id, value);
      else await updateAdminClaimStatus(id, value);
      load();
    } catch (e) { setError(e.response?.data?.error || 'Could not update status.'); }
  };
  const addAdjustment = async (e) => {
    e.preventDefault();
    try {
      const cents = Math.round(Number(adjustment.amount) * 100);
      await createRideAdjustment(selected.ride.id, { target: adjustment.target, amount_cents: cents, reason: adjustment.reason, currency: cur(selected.ride) || undefined });
      setAdjustment({ target: 'customer', amount: '', reason: '' });
      openRide(selected.ride.id);
    } catch (err) { setError(err.response?.data?.error || 'Could not create adjustment.'); }
  };
  const reverse = async (id) => {
    try { await reverseRideAdjustment(selected.ride.id, id, {}); openRide(selected.ride.id); }
    catch (err) { setError(err.response?.data?.error || 'Could not reverse adjustment.'); }
  };

  const r = selected?.ride;
  return (
    <div style={s.page}>
      <h1 style={s.title}>Ride Operations</h1>
      <p style={s.hint}>Monitor rides, drivers, waiting time, payments and problem cases. Every admin action here is logged.</p>
      <div style={s.tabs}>{[['rides', 'Rides'], ['payouts', 'Driver payouts'], ['claims', 'Payment exceptions']].map(([key, label]) =>
        <button key={key} style={tab === key ? s.activeTab : s.tab} onClick={() => { setTab(key); setSelected(null); }}>{label}</button>)}</div>

      {tab === 'rides' && (
        <>
          <div style={s.stageRow}>
            {[['', 'All'], ['requested', 'Requested'], ['assigned', 'Assigned'], ['ongoing', 'Ongoing'], ['completed', 'Completed']].map(([k, l]) => (
              <button key={k || 'all'} style={(filters.stage || '') === k ? s.activeTab : s.tab} onClick={() => setFilter('stage', k)}>{l}</button>
            ))}
          </div>
          <div style={s.filters}>
            <select style={s.input} value={filters.payment_method || ''} onChange={(e) => setFilter('payment_method', e.target.value)}><option value="">All payment types</option><option value="cash">Cash</option><option value="digital">Digital</option></select>
            <input style={s.input} type="date" value={filters.from || ''} onChange={(e) => setFilter('from', e.target.value)} aria-label="From" />
            <input style={s.input} type="date" value={filters.to || ''} onChange={(e) => setFilter('to', e.target.value)} aria-label="To" />
            <label style={s.check}><input type="checkbox" checked={filters.problems_only === 'true'} onChange={(e) => setFilter('problems_only', e.target.checked ? 'true' : '')} /> Problem cases only</label>
            <label style={s.check}><input type="checkbox" checked={filters.cash_pending === 'true'} onChange={(e) => setFilter('cash_pending', e.target.checked ? 'true' : '')} /> Cash not confirmed</label>
            <button style={s.tab} onClick={() => load()}>{loading ? 'Loading…' : 'Refresh'}</button>
          </div>
        </>
      )}
      {error && <div style={s.error}>{error}</div>}

      <div style={s.tableWrap}>
        <table style={s.table}>
          <thead><tr>{(tab === 'rides'
            ? ['Trip', 'Stage', 'Customer', 'Driver', 'Pickup / waiting', 'Price', 'Payment status', 'Problems']
            : tab === 'payouts' ? ['Driver', 'Amount', 'Fee', 'Status', 'Created'] : ['Trip', 'Driver', 'Amount', 'Status', 'Created']).map((h) => <th style={s.th} key={h}>{h}</th>)}</tr></thead>
          <tbody>{rows.map((row) => (tab === 'rides' ? (
            <tr key={row.id} onClick={() => openRide(row.id)} style={s.click}>
              <td style={s.td}><strong>{row.trip_number || row.id.slice(0, 8)}</strong><br /><small style={s.small}>{when(row.created_at)}</small></td>
              <td style={s.td}>{row.stage}<br /><small style={s.small}>{row.status}{row.outcome ? ` · ${row.outcome}` : ''}</small></td>
              <td style={s.td}>{person(row.customer)}</td>
              <td style={s.td}>{row.driver ? <>{person(row.driver)}<br /><small style={{ ...s.small, color: row.driver.driver_online ? '#166534' : '#991b1b' }}>{row.driver.driver_online ? 'Online' : 'Offline'}</small>{row.vehicle && <><br /><small style={s.small}>{row.vehicle.make} {row.vehicle.model} · {row.vehicle.license_plate}</small></>}</> : '—'}</td>
              <td style={s.td}>{row.arrival_at ? <>Arrived {new Date(row.arrival_at).toLocaleTimeString()}<br /><small style={s.small}>{row.waiting_running ? `Waiting ${mmss(row.waiting_seconds)} (running)` : row.waiting_seconds != null ? `Waited ${mmss(row.waiting_seconds)}` : ''}</small></> : '—'}</td>
              <td style={s.td}>{money(row.customer_total_cents ?? Math.round(Number(row.total_amount || 0) * 100), cur(row))}</td>
              <td style={s.td}>{row.payment_label}</td>
              <td style={s.td}>{row.problems?.length ? row.problems.map((p) => <span key={p} style={s.problem}>{p}</span>) : <small style={s.small}>—</small>}</td>
            </tr>
          ) : (
            <tr key={row.id}>
              <td style={s.td}>{tab === 'payouts' ? person(row.driver) : (row.ride?.trip_number || row.ride_id)}</td>
              <td style={s.td}>{tab === 'payouts' ? money(row.amount_cents, row.currency) : person(row.driver)}</td>
              <td style={s.td}>{tab === 'payouts' ? money(row.fee_cents, row.currency) : money(Number(row.amount_cents || 0) + Number(row.fee_cents || 0), row.currency)}</td>
              <td style={s.td}><select style={s.input} value={row.status} onChange={(e) => updateStatus(row.id, e.target.value)}>{(tab === 'payouts' ? ['requested', 'processing', 'paid', 'failed', 'rejected'] : ['open', 'under_review', 'paid', 'rejected']).map((v) => <option key={v}>{v}</option>)}</select></td>
              <td style={s.td}>{when(row.created_at)}</td>
            </tr>
          )))}</tbody>
        </table>
        {!rows.length && <div style={s.empty}>{loading ? 'Loading…' : 'No records found.'}</div>}
      </div>

      {r && (
        <aside style={s.panel} aria-label="Ride details">
          <button style={s.close} onClick={() => setSelected(null)}>Close</button>
          <h2 style={{ marginTop: 0 }}>{r.trip_number || r.id}</h2>
          <Section title="Ride">
            <Row k="Stage" v={`${r.stage} (${r.status}${r.outcome ? `, ${r.outcome}` : ''})`} />
            <Row k="Customer" v={person(r.customer)} />
            <Row k="Driver" v={r.driver ? `${person(r.driver)} · ${r.driver.driver_online ? 'Online' : 'Offline'}` : '—'} />
            <Row k="Vehicle" v={r.vehicle ? `${r.vehicle.make || ''} ${r.vehicle.model || ''} · ${r.vehicle.license_plate || ''}` : '—'} />
            <Row k="From" v={r.pickup_address} /><Row k="To" v={r.dropoff_address} />
          </Section>
          <Section title="Price">
            <Row k="Total" v={money(r.customer_total_cents, cur(r))} />
            <Row k="Driver amount" v={money(r.driver_trip_amount_cents, cur(r))} />
            <Row k="NULA MOVE fee" v={money(r.nula_fee_cents, cur(r))} />
          </Section>
          <Section title="Pickup and waiting">
            <Row k="Arrived at pickup" v={when(r.arrival_at)} />
            <Row k="Waiting" v={r.waiting_running ? `${mmss(r.waiting_seconds)} (running)` : mmss(r.waiting_seconds)} />
            <Row k="Chargeable waiting" v={mmss(r.wait_chargeable_seconds)} />
            <Row k="Waiting charges" v={`${money(r.waiting_amount_cents, cur(r))} driver + ${money(r.waiting_fee_cents, cur(r))} fee`} />
          </Section>
          <Section title="Payment">
            <Row k="Status" v={r.payment_label} />
            {r.payment_method === 'cash' && <Row k="Confirmed amounts" v={`customer ${money(r.cash_customer_amount_cents, cur(r))} · driver ${money(r.cash_driver_amount_cents, cur(r))}`} />}
            {r.payment_exception && <Row k="Exception" v={r.payment_exception} />}
            {(selected.payments || []).map((p) => <Row key={p.id} k={p.kind || 'payment'} v={`${money(p.amount_cents ?? Math.round(Number(p.amount || 0) * 100), p.currency_code || cur(r))} · ${p.status}${p.failure_message ? ` · ${p.failure_message}` : ''}`} />)}
          </Section>
          {r.problems?.length > 0 && <Section title="Problem cases">{r.problems.map((p) => <span key={p} style={s.problem}>{p}</span>)}</Section>}
          <Section title="Fees from cash and offsets">
            {(selected.receivables || []).map((x) => <Row key={x.id} k={x.receivable_number || 'Outstanding fee'} v={`${money(x.amount_cents, x.currency)} · ${x.status}`} />)}
            {(selected.offsets || []).map((o) => <Row key={o.id} k="Offset" v={`${money(o.amount_cents, o.currency)} · from ${o.receivable?.receivable_number || o.receivable?.booking_id || '—'}`} />)}
            {!(selected.receivables || []).length && !(selected.offsets || []).length && <p style={s.small}>None</p>}
          </Section>
          <Section title="Adjustments">
            {(selected.adjustments || []).map((a) => (
              <div style={s.item} key={a.id}>{a.target} · {money(a.amount_cents, a.currency)} · {a.reason}
                {!a.reverses_id && !(selected.adjustments || []).some((x) => x.reverses_id === a.id) && <button style={s.mini} onClick={() => reverse(a.id)}>Reverse</button>}
              </div>
            ))}
            <form onSubmit={addAdjustment} style={s.form}>
              <select style={s.input} value={adjustment.target} onChange={(e) => setAdjustment({ ...adjustment, target: e.target.value })}><option>customer</option><option>driver</option><option>platform</option></select>
              <input style={s.input} required type="number" step="0.01" placeholder={`Amount (${cur(r)})`} value={adjustment.amount} onChange={(e) => setAdjustment({ ...adjustment, amount: e.target.value })} />
              <input style={{ ...s.input, flex: '1 1 160px' }} required placeholder="Reason" value={adjustment.reason} onChange={(e) => setAdjustment({ ...adjustment, reason: e.target.value })} />
              <button style={s.activeTab}>Add adjustment</button>
            </form>
          </Section>
          <Section title="History of status changes">
            {(selected.events || []).map((e) => <Row key={e.id} k={when(e.created_at)} v={`${e.event_type.replace(/_/g, ' ')}${e.actor ? ` · ${person(e.actor)}` : ''}${e.lat != null ? ` · GPS ${e.lat}, ${e.lng}` : ''}`} />)}
            {!(selected.events || []).length && <p style={s.small}>No events recorded.</p>}
          </Section>
          <Section title="Admin interventions (log)">
            {(selected.interventions || []).map((l) => <Row key={l.id} k={when(l.created_at)} v={`${l.action.replace(/_/g, ' ')} · ${person(l.admin)}`} />)}
            {!(selected.interventions || []).length && <p style={s.small}>None</p>}
          </Section>
        </aside>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return <div style={s.section}><h3 style={s.h3}>{title}</h3>{children}</div>;
}
function Row({ k, v }) {
  return <div style={s.row}><span style={s.small}>{k}</span><span style={{ textAlign: 'right', overflowWrap: 'anywhere' }}>{v || '—'}</span></div>;
}

const s = {
  page: { padding: 'clamp(12px, 3vw, 24px)', maxWidth: 1400, boxSizing: 'border-box' },
  title: { margin: 0, fontSize: 24 }, hint: { color: '#64748b' },
  tabs: { display: 'flex', flexWrap: 'wrap', gap: 8, margin: '18px 0' },
  stageRow: { display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 },
  tab: { padding: '8px 12px', border: '1px solid #cbd5e1', background: '#fff', borderRadius: 8, cursor: 'pointer' },
  activeTab: { padding: '8px 12px', border: '1px solid #0f766e', background: '#0f766e', color: '#fff', borderRadius: 8, cursor: 'pointer' },
  filters: { display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14, alignItems: 'center' },
  input: { padding: '7px 9px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, minWidth: 0 },
  check: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 },
  error: { padding: 10, background: '#fef2f2', color: '#991b1b', borderRadius: 8, marginBottom: 12 },
  tableWrap: { overflowX: 'auto', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12 },
  table: { width: '100%', borderCollapse: 'collapse', minWidth: 960 },
  th: { textAlign: 'left', padding: 12, background: '#f8fafc', borderBottom: '1px solid #e2e8f0', fontSize: 13 },
  td: { padding: 12, borderBottom: '1px solid #f1f5f9', fontSize: 13, verticalAlign: 'top' },
  small: { fontSize: 12, color: '#64748b' },
  click: { cursor: 'pointer' }, empty: { padding: 24, color: '#64748b' },
  problem: { display: 'inline-block', fontSize: 11.5, fontWeight: 700, background: '#fee2e2', color: '#991b1b', borderRadius: 999, padding: '3px 8px', margin: '0 4px 4px 0' },
  panel: { position: 'fixed', right: 0, top: 0, height: '100vh', width: 'min(560px, 100vw)', overflow: 'auto', background: '#fff', borderLeft: '1px solid #cbd5e1', padding: 20, boxSizing: 'border-box', boxShadow: '-10px 0 30px rgba(0,0,0,.15)', zIndex: 20 },
  close: { float: 'right', padding: '6px 10px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer' },
  section: { borderTop: '1px solid #f1f5f9', padding: '10px 0' },
  h3: { fontSize: 14, margin: '0 0 6px' },
  row: { display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 13, padding: '3px 0' },
  item: { padding: '6px 0', borderBottom: '1px solid #f1f5f9', fontSize: 13 },
  mini: { marginLeft: 8, fontSize: 12, padding: '3px 8px', borderRadius: 6, border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer' },
  form: { display: 'flex', flexWrap: 'wrap', gap: 6, margin: '10px 0 4px' },
};
